import { flowRight } from 'lodash/fp';
import { withSnackbar } from 'notistack';
import { withCompanyContext } from '../CompanyContext';
import { withStyles } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../../../api';
import formatters from '../../../formatters';
import EditButton from '../../../ui/EditButton';
import DetailsButton from '../../../ui/DetailsButton';
import Dashboard from 'ui/dashboard/Dashboard';
import { Grid, Typography } from '@material-ui/core';
import SharesImg from '../../../images/shares-165x165.png';
import DashboardBox from 'ui/dashboard/Box';
import DashboardBoxContent from 'ui/dashboard/BoxContent';
import { MTable } from 'ui/mtable/MTable';
import VaultForm from './VaultForm';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';
import {
    EDIT_VAULT,
    DELETE_VAULTS,
    DELETE_VAULT_WITHOUT_RECIPIENTS,
} from './constants';

const vaultsStyles = theme => ({
    button: {
        margin: theme.spacing(),
        fontSize: '10px',
    },
    leftIcon: {
        marginRight: theme.spacing(),
        fontSize: '15px',
    },
});

function Vaults(props) {
    const [vaults, setVaults] = useState([]);
    const [selectedVaults, setSelectVaults] = useState([]);
    const [vaultsLoading, setVaultsLoading] = useState(true);
    const [currentVault, setCurrentVault] = useState(null);
    const [currentAction, setCurrentAction] = useState(null);
    const [formErrors, setFormErrors] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const { company, editable } = props;

    const fetchVaults = useCallback(async () => {
        if (!company?.id) {
            setVaultsLoading(false);
            return;
        }
        try {
            const response = await api.get(
                `/api/organization/${company?.id}/vaults`
            );
            setVaults(response.data?.vaults);
            setLoaded(true);
        } catch (error) {
            setLoaded(false);
        } finally {
            setVaultsLoading(false);
        }
    }, [company.id]);

    const fetchVault = ({ uuid }) => {
        const data = {
            vault_uuid: uuid,
        };

        api.get(`/api/organization/${company.id}/vaults`, data).then(json => {
            setCurrentAction(EDIT_VAULT);
            setCurrentVault(json.data.vaults);
        });
    };

    useEffect(() => {
        void fetchVaults();
    }, [fetchVaults]);

    const formatRecipientsForCsv = recipients => {
        const transformed_elements = recipients.map((recipient, index) => {
            return recipient.name;
        });
        return transformed_elements.join(',');
    };

    const formatRecipients = recipients => {
        return recipients.map((recipient, index) => {
            let result = [];
            result.push(<span key={index}>{recipient.name}</span>);
            if (index < recipients.length - 1) {
                result.push(<span key={index + 'comma'}>, </span>);
            }
            return result;
        });
    };

    const handleAction = currentAction => values => {
        setCurrentAction(currentAction);
        setSelectVaults(values);
    };

    const actions = useMemo(() => {
        return [
            {
                key: DELETE_VAULTS,
                name: 'Delete vaults',
                action: handleAction(DELETE_VAULTS),
            },
        ];
    }, []);

    const actionMenuItem = () => {
        const availableActions = selectedVaults.length ? actions : [];

        return availableActions.length > 0
            ? availableActions.map(action => ({
                  key: action.key,
                  name: action.name,
                  onClick: dropdownCloseCallback => {
                      action.action(selectedVaults);
                      dropdownCloseCallback();
                  },
              }))
            : [
                  {
                      key: 'no',
                      name: 'No common action',
                      onClick: dropdownCloseCallback => {
                          dropdownCloseCallback();
                      },
                  },
              ];
    };

    const handleEdit = ({ uuid }) => {
        fetchVault({ uuid });
    };

    const updateVault = vaultData => {
        vaultData.organization = company.id;

        api.patch(`/api/organization/${company.id}/vaults`, vaultData).then(
            json => {
                if (json.errors) {
                    setFormErrors(json.error);
                } else {
                    showInfoSnackbar('Vault will be updated.');
                    setVaultsLoading(true);
                    setCurrentAction(null);
                    setCurrentVault(null);
                    void fetchVaults();
                }
            }
        );
    };

    const handleFormCancel = () => {
        setFormErrors(null);
        setCurrentVault(null);
    };

    const getColumns = () => {
        return [
            {
                field: 'uuid',
                title: 'Uuid',
                hidden: true,
                notFilter: true,
                export: false,
            },
            {
                field: 'nickname',
                title: 'Name',
                render: row => <strong>{row.nickname}</strong>,
                filterType: 'select',
                cellStyle: {
                    minWidth: '15%',
                },
            },
            {
                field: 'owners',
                title: 'Owners',
                filterType: 'select',
                cellStyle: {
                    minWidth: '17%',
                },
            },
            {
                field: 'recipient_count',
                title: '# of Recipients',
                cellStyle: {
                    minWidth: '7%',
                },
            },
            {
                field: 'recipients',
                title: 'Recipients',
                render: row => {
                    const withoutOwner = row.recipients.filter(
                        item => item.permissions !== 'owner'
                    );

                    return formatRecipients(withoutOwner);
                },
                exportValue: val => {
                    const withoutOwner = val.filter(
                        item => item.permissions !== 'owner'
                    );

                    return formatRecipientsForCsv(withoutOwner);
                },
                filterType: 'select',
                filterOptions: data => {
                    data = data.map(item =>
                        item.recipients.map(rec => rec.name)
                    );
                    return [].concat(...data);
                },
                filter: (value, filter) =>
                    value.find(item => filter.value === item.name),
                cellStyle: {
                    minWidth: '43%',
                },
            },
            {
                field: 'last_modified_date',
                title: 'Last Update',
                filterType: 'select',
                render: row => formatters.date(row.last_modified_date),
                cellStyle: {
                    minWidth: '10%',
                },
            },
            {
                field: 'action',
                notFilter: true,
                title: '',
                render: row => {
                    if (editable) {
                        return <EditButton onClick={() => handleEdit(row)} />;
                    }
                    return <DetailsButton onClick={() => handleEdit(row)} />;
                },
                cellStyle: {
                    minWidth: '8%',
                },
            },
        ];
    };

    const handleSelectionChange = val => {
        setSelectVaults(val);
    };

    const showTable = useMemo(() => {
        return vaults?.length > 0 || (vaults?.length <= 0 && !loaded);
    }, [vaults, loaded]);

    const handleCloseDialog = () => () => {
        setCurrentAction(null);
    };

    const deleteVaults = () => {
        const data = selectedVaults.map(({ uuid }) => uuid);
        setVaultsLoading(true);
        showInfoSnackbar('Vault(s) will be deleted.');
        api.remove(`/api/organization/${company.id}/vaults`, data).then(
            json => {
                void fetchVaults();
            }
        );
    };

    const showInfoSnackbar = (msg, delay = null) => {
        let options = {
            variant: 'info',
        };
        if (delay) {
            options['autoHideDuration'] = delay;
        }
        props.enqueueSnackbar(msg, options);
    };

    const renderDialog = () => {
        const dialog = {
            [DELETE_VAULTS]: {
                title: 'Confirm delete',
                warning:
                    'Deleting a vault will permanently remove it and its items for you and all recipients. This operation cannot be undone.',
                confirmByTyping: 'Understood',
            },
        };
        const callbacks = {
            [DELETE_VAULTS]: { callback: deleteVaults },
        };

        if (currentAction === EDIT_VAULT && currentVault) {
            return (
                <VaultForm
                    open={true}
                    editable={editable}
                    currentVault={currentVault}
                    formErrors={formErrors}
                    vaults={vaults}
                    onCancel={() => {
                        setCurrentAction(null);
                        handleFormCancel();
                    }}
                    onOk={vaultData => {
                        updateVault(vaultData);
                    }}
                    onDelete={(action = DELETE_VAULTS) => {
                        handleAction(action)([currentVault]);
                    }}
                />
            );
        }

        if (currentAction === DELETE_VAULT_WITHOUT_RECIPIENTS) {
            deleteVaults();
            setCurrentAction(null);
            return null;
        }

        if (dialog[currentAction]) {
            return (
                <ConfirmationDialog
                    open={true}
                    {...dialog[currentAction]}
                    data={selectedVaults.map(item => item.nickname)}
                    onCancel={handleCloseDialog(currentAction)}
                    onOk={handleOkDialog(callbacks[currentAction])}
                />
            );
        }
    };

    const handleOkDialog = ({ callback }) => () => {
        setCurrentAction(null);
        callback(selectedVaults);
    };

    return (
        <>
            {Boolean(currentAction) && renderDialog()}
            {showTable && (
                <MTable
                    stateKey="Vaults"
                    columns={getColumns()}
                    data={vaults}
                    loading={vaultsLoading}
                    title={null}
                    actionMenuItems={actionMenuItem()}
                    onSelectionChange={handleSelectionChange}
                    options={{ selection: editable }}
                />
            )}
            {loaded && !vaults.length && (
                <Dashboard width="70" align="center">
                    <DashboardBox>
                        <DashboardBoxContent>
                            <Grid container spacing={3} direction="row">
                                <Grid item xs="auto">
                                    <img src={SharesImg} alt="Vaults" />
                                </Grid>
                                <Grid item xs>
                                    <Grid
                                        container
                                        justifyContent="center"
                                        direction="column"
                                        style={{ height: '100%' }}
                                    >
                                        <Typography variant="h5" gutterBottom>
                                            Vaults created by users will appear
                                            here
                                        </Typography>
                                        <Typography
                                            color="textSecondary"
                                            variant="body1"
                                            gutterBottom
                                        >
                                            When users create vaults using the
                                            CyberFOX Password Manager
                                            application the vaults will appear
                                            here, where you can manage them.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </DashboardBoxContent>
                    </DashboardBox>
                </Dashboard>
            )}
        </>
    );
}

export default flowRight(
    withSnackbar,
    withCompanyContext,
    withStyles(vaultsStyles)
)(Vaults);
